import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dumbbell, Heart, Camera, ArrowRight, CheckCircle, Shield, Zap, Trophy } from "lucide-react";

const LOGO_TN = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/prm3jnbc_IMG_1264.png";
const LOGO_HF = "https://customer-assets.emergentagent.com/job_6b5e3bef-198e-4658-8305-ef4fd544fb4d/artifacts/o4ok88eo_IMG_1930.png";

const HomePage = () => {
  const services = [
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: "Youth Performance Training",
      description: "Safe, professional, results-driven training for youth athletes. Speed, agility, strength, and sport-specific development.",
      link: "/training",
      color: "from-red-600 to-red-700"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Sports Massage Therapy",
      description: "Recovery-focused therapeutic massage. Reduce soreness, prevent injuries, and stay game-ready.",
      link: "/massage",
      color: "from-red-700 to-red-800"
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "Sports Media Services",
      description: "Professional photography and videography. Highlight reels, action shots, and content that gets you noticed.",
      link: "/media",
      color: "from-red-800 to-red-900"
    }
  ];

  const features = [
    { icon: <Shield className="w-5 h-5" />, text: "Safe & Professional Training" },
    { icon: <Zap className="w-5 h-5" />, text: "Results-Driven Programs" },
    { icon: <Trophy className="w-5 h-5" />, text: "Long-Term Athletic Development" },
    { icon: <Heart className="w-5 h-5" />, text: "Expert Recovery Services" }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[90vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 athletic-pattern"></div>
        
        {/* Diagonal accent */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#E10600]/10 to-transparent transform skew-x-12"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <p className="text-[#E10600] font-bold uppercase tracking-widest mb-4">topnotchhf.com</p>
              <h1 className="hero-title text-white mb-4">
                PERFORMANCE.<br />
                <span className="text-[#E10600]">RECOVERY.</span>
              </h1>
              <p className="text-xl text-gray-300 mb-2">Train like an athlete. Recover like a pro.</p>
              <p className="text-gray-400 mb-8 max-w-lg">
                Safe, professional, and results-driven training for youth athletes of all levels. Expert sports massage therapy for optimal recovery.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/booking?category=training" data-testid="hero-book-training">
                  <Button className="cta-btn-primary text-base">
                    Book Training
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/booking?category=massage" data-testid="hero-book-massage">
                  <Button className="cta-btn-secondary text-base">
                    Book Massage
                  </Button>
                </Link>
                <Link to="/booking?category=media" data-testid="hero-book-media">
                  <Button className="cta-btn-secondary text-base">
                    Book Media
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Logo */}
            <div className="hidden lg:flex flex-col items-center justify-center gap-6 slide-in-left">
              <img src={LOGO_TN} alt="Top Notch" className="w-64 md:w-80 drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider"></div>

      {/* Services Section */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4">OUR SERVICES</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Everything athletes need to train harder, recover faster, and perform better.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Link to={service.link} key={index} data-testid={`service-card-${index}`}>
                <div className="service-card h-full">
                  <div className={`feature-icon bg-gradient-to-br ${service.color}`}>
                    {service.icon}
                  </div>
                  <h3 className="font-display text-2xl text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 text-sm">{service.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl text-white mb-6">
                WHY <span className="text-[#E10600]">TOP NOTCH?</span>
              </h2>
              <p className="text-gray-400 mb-8">
                We're not a generic gym. We specialize in youth athletic development and sports recovery - programs designed by athletes, for athletes.
              </p>
              
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E10600]/20 flex items-center justify-center text-[#E10600]">
                      {feature.icon}
                    </div>
                    <span className="text-white font-medium">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link to="/booking" data-testid="why-us-book-btn">
                  <Button className="cta-btn-primary">
                    Start Training Today
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-video bg-[#1a1a1a] rounded-lg border border-[#2A2A2A] flex items-center justify-center">
                <div className="text-center p-8">
                  <p className="font-display text-3xl text-white mb-2">TRAIN WITH PURPOSE</p>
                  <p className="text-[#E10600] font-semibold">Youth | Middle School | High School</p>
                </div>
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#E10600] rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Youth Focus Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#111111] to-[#1a1a1a] rounded-xl p-8 md:p-12 border border-[#2A2A2A]">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-3xl text-white mb-4">YOUTH ATHLETE DEVELOPMENT</h3>
                <p className="text-gray-400 mb-4">
                  Our programs focus on long-term athletic development, injury risk reduction, and performance gains that transfer directly to sport.
                </p>
                <ul className="space-y-2">
                  {["Proper movement & technique", "Age-appropriate training", "Progress tracking", "Coach collaboration"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#E10600]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <p className="text-[#E10600] font-display text-6xl mb-2">$55</p>
                <p className="text-gray-400">Starting at per session</p>
                <Link to="/training" className="inline-block mt-4">
                  <Button variant="outline" className="border-[#E10600] text-[#E10600] hover:bg-[#E10600] hover:text-white">
                    View Training Programs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#E10600] to-[#B80500] relative overflow-hidden">
        <div className="absolute inset-0 athletic-pattern opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-3xl md:text-5xl text-white mb-4">
            SERIOUS ATHLETES TRAIN WITH INTENTION.
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Don't wait for game day to get serious. Book your session now and start performing at the level you deserve.
          </p>
          <Link to="/booking" data-testid="cta-book-btn">
            <Button className="bg-white text-[#0A0A0A] hover:bg-gray-100 font-bold text-lg px-8 py-6">
              BOOK YOUR SESSION
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
